/**
* @file assets/product-form.js
* @description Product-page form behaviour: the <product-page-quantity-input> custom
* element (whole numbers, never below 1), variant selection sync (the form's id field,
* price, detail rows, purchasing state) from the [data-product-variants-json]
* ({id, options, price, available} per variant) and [data-product-variant-details-json]
* payloads rendered by sections/product.liquid, and the AJAX add-to-cart submission
* through window.CartRefreshOrchestrator.runMutation. The id field is a hidden input
* for a product with one variant, else the select over the variants that a reader
* without JavaScript uses (product.css hides it under html.js, L-67); the script writes
* the variant the option selects match into either. The price mirrors the section:
* the variant price when the product is out of stock or the variant is available,
* otherwise "From" data-product-price-min. The add button has one label source: the
* base label captured once per form, replaced by "Sold out" / "Unavailable" from the
* selected variant and by the transient submit labels, after which the purchasing
* state is re-derived; a selection made while an add is in flight or its result label
* shows updates the id, price and detail rows but leaves the button, disabled, to the
* submit flow (P-7, L-64). The price sync is
* skipped for a variant without "price" (commission and enquiry pages, which hide the
* price, emit none). A failed add writes its reason into the form's own
* [data-product-form-message] live region (the cart panel's region is hidden while a
* product shows), cleared when the next submit starts and on success. Per-form state
* (initialisation, the in-flight submit, the base label, the variant state) lives in
* module WeakSets and WeakMaps, never in data-* attributes: dynamic-content.js caches a
* served panel's markup after this script has run on it, so a copy of the markup is a
* new form and is initialised afresh. Loaded on the index and product templates
* directly after cart-component.js. Registers its initialiser with
* CartRefreshOrchestrator.registerBehavior so initializeBehaviors(root) also covers
* product markup injected by dynamic-content.js, and runs it for the current document.
* Wrapped in an IIFE; window gets nothing beyond the custom element. Requires
* window.ThemeDom, window.ThemeFormatters and window.CartRefreshOrchestrator.
*/

(function () {
'use strict';

const { onReady, queryWithin, normalizeWhitespace, storeUrl } = window.ThemeDom;
const { applyDetailRow, formatDimensions, formatMoney, getMoneyFormat, parseWholeNumber } = window.ThemeFormatters;
const orchestrator = window.CartRefreshOrchestrator;

const DEFAULT_ADD_BUTTON_LABEL = 'Add to cart';
const SOLD_OUT_LABEL = 'Sold out';
const SUBMIT_RESULT_LABEL_DURATION = 1200;

// Per-form state keyed by the form element: the base add-button label (captured once
// from the rendered markup); for forms with variant selectors, the variant sync state,
// whose presence also marks the selectors as wired, so the post-submit reset can
// re-derive the purchasing state; the forms whose submit handler is attached; and the
// forms with an add in flight. None of it is written into the markup (H-12):
// dynamic-panel-loader.js adopt() caches a served panel's innerHTML after this script
// has run, and a data-* flag copied into that cache would leave the re-shown form
// unwired (inert size selector, native submit) or blocked. A copy is a new element, so
// it is initialised afresh, while a repeated run on the same nodes stays a no-op.
const productFormBaseLabels = new WeakMap();
const productFormVariantStates = new WeakMap();
const productFormsWithAjaxSubmit = new WeakSet();
const submittingProductForms = new WeakSet();

function parseJsonFromScriptElement(scriptElement, payloadLabel) {
    if (!scriptElement) {
        return null;
    }

    const jsonSource = String(scriptElement.textContent || '').trim();

    if (!jsonSource) {
        return null;
    }

    try {
        return JSON.parse(jsonSource);
    } catch (error) {
        console.warn(`Unable to parse the ${payloadLabel} JSON payload.`, error);
        return null;
    }
}

function normalizeVariantOptionValue(value) {
    return normalizeWhitespace(value).toLowerCase();
}

function findVariantBySelectedOptions(variants, selectedOptions) {
    if (!Array.isArray(variants) || !variants.length) {
        return null;
    }

    if (!Array.isArray(selectedOptions) || !selectedOptions.length) {
        return null;
    }

    const normalizedSelectedOptions = selectedOptions.map((selectedOption) => {
        return normalizeVariantOptionValue(selectedOption);
    });

    return variants.find((variant) => {
        if (!variant || !Array.isArray(variant.options)) {
            return false;
        }

        if (variant.options.length !== normalizedSelectedOptions.length) {
            return false;
        }

        return normalizedSelectedOptions.every((selectedOption, optionIndex) => {
            return normalizeVariantOptionValue(variant.options[optionIndex]) === selectedOption;
        });
    }) || null;
}

// The product form sits inside .product-info__container (sections/product.liquid).
function getProductInfoContainerForForm(form) {
    return form ? form.closest('.product-info__container') : null;
}

function getProductVariantDetailsListForForm(form) {
    const productInfoContainer = getProductInfoContainerForForm(form);

    return productInfoContainer
        ? productInfoContainer.querySelector('[data-product-details-list]')
        : null;
}

function findProductVariantDetailsById(variantDetails, variantId) {
    if (!Array.isArray(variantDetails) || !variantDetails.length || !variantId) {
        return null;
    }

    const normalizedVariantId = String(variantId);

    return variantDetails.find((details) => {
        return details && String(details.id) === normalizedVariantId;
    }) || null;
}

function syncProductVariantDetails(formState, matchingVariant) {
    const { form, variantDetails } = formState;
    const detailsList = getProductVariantDetailsListForForm(form);

    if (!detailsList || !matchingVariant) {
        return;
    }

    const matchingDetails = findProductVariantDetailsById(variantDetails, matchingVariant.id);

    if (!matchingDetails) {
        return;
    }

    applyDetailRow(detailsList, 'dimensions', formatDimensions(matchingDetails));
    applyDetailRow(detailsList, 'weight', matchingDetails.weight);
    applyDetailRow(detailsList, 'light-output', matchingDetails.light_output);
    applyDetailRow(detailsList, 'runtime', matchingDetails.runtime);
}

// Mirrors the price branch in sections/product.liquid: the variant price when the
// product is out of stock (data-product-out-of-stock) or the variant is available,
// otherwise "From" the lowest price (data-product-price-min, in cents). A variant
// without "price" (commission and enquiry pages, P-8) leaves the page untouched.
function syncProductVariantPrice(formState, matchingVariant) {
    const productInfoContainer = getProductInfoContainerForForm(formState.form);

    if (!productInfoContainer || !matchingVariant || typeof matchingVariant.price === 'undefined') {
        return;
    }

    const priceElement = productInfoContainer.querySelector('.product-info__price');

    if (!priceElement) {
        return;
    }

    const moneyFormat = getMoneyFormat(priceElement);
    const isOutOfStock = priceElement.dataset.productOutOfStock === 'true';
    const priceMinSource = String(priceElement.dataset.productPriceMin || '').trim();
    const priceMin = /^\d+$/.test(priceMinSource) ? Number(priceMinSource) : null;

    if (isOutOfStock || matchingVariant.available || priceMin === null) {
        priceElement.innerHTML = formatMoney(matchingVariant.price, moneyFormat);
        return;
    }

    priceElement.innerHTML = `From ${formatMoney(priceMin, moneyFormat)}`;
}

// sections/product.liquid renders <button name="add"><span>label</span></button>.
function getAddButtonForForm(form) {
    return form.querySelector('button[name="add"]');
}

function getAddButtonLabelElement(addButton) {
    return addButton ? (addButton.querySelector('span') || addButton) : null;
}

// The base label is the server-rendered text, captured on first use before any
// script writes to it. The section renders "Sold out" when the initially selected
// variant is unavailable; that is a state, not the base label.
function getProductFormBaseLabel(form) {
    if (!productFormBaseLabels.has(form)) {
        const labelElement = getAddButtonLabelElement(getAddButtonForForm(form));
        const renderedLabel = labelElement ? String(labelElement.textContent || '').trim() : '';

        productFormBaseLabels.set(form, renderedLabel && renderedLabel !== SOLD_OUT_LABEL
            ? renderedLabel
            : DEFAULT_ADD_BUTTON_LABEL);
    }

    return productFormBaseLabels.get(form);
}

function setProductFormPurchasingState(formState, isAvailable, unavailableLabel = SOLD_OUT_LABEL) {
    const {
        form,
        addButton,
        quantityInput,
        quantityButtons
    } = formState;
    const addButtonLabelElement = getAddButtonLabelElement(addButton);

    if (addButton) {
        addButton.disabled = !isAvailable;

        if (isAvailable) {
            addButton.removeAttribute('aria-disabled');
        } else {
            addButton.setAttribute('aria-disabled', 'true');
        }
    }

    if (addButtonLabelElement) {
        addButtonLabelElement.textContent = isAvailable
            ? getProductFormBaseLabel(form)
            : unavailableLabel;
    }

    if (quantityInput) {
        if (isAvailable && (!quantityInput.value || Number(quantityInput.value) < 1)) {
            quantityInput.value = '1';
        }

        quantityInput.disabled = !isAvailable;
    }

    quantityButtons.forEach((button) => {
        button.disabled = !isAvailable;
    });
}

// While an add is in flight or its result label shows (submittingProductForms) a
// selection still moves the id field, the price and the detail rows, but the add button
// keeps "Adding…", "Added" or "Error" and stays disabled (P-7, L-64):
// resetProductFormSubmitState re-runs this after the result label and derives the
// purchasing state from the variant selected by then.
function syncProductFormVariantSelection(formState) {
    const {
        form,
        variantIdField,
        variantSelectors,
        variants
    } = formState;
    const selectedOptions = variantSelectors.map((selector) => selector.value);
    const matchingVariant = findVariantBySelectedOptions(variants, selectedOptions);
    const isSubmitting = submittingProductForms.has(form);

    if (!matchingVariant) {
        if (!isSubmitting) {
            setProductFormPurchasingState(formState, false, 'Unavailable');
        }
        return;
    }

    variantIdField.value = String(matchingVariant.id);
    syncProductVariantDetails(formState, matchingVariant);
    syncProductVariantPrice(formState, matchingVariant);

    if (!isSubmitting) {
        setProductFormPurchasingState(formState, Boolean(matchingVariant.available));
    }
}

function initializeProductVariantControls(root = document) {
    queryWithin(root, 'form').forEach((form) => {
        if (productFormVariantStates.has(form)) {
            return;
        }

        const variantSelectors = Array.from(form.querySelectorAll('.product-variant-select'));

        if (!variantSelectors.length) {
            return;
        }

        // The hidden input, or the variant select served for readers without JavaScript
        // (L-67); both take the matching variant's id as their value.
        const variantIdField = form.querySelector('[name="id"]');

        if (!variantIdField) {
            return;
        }

        // One {id, options, price, available} object per variant (no price on
        // commission and enquiry pages); the section emits nothing else, so add a field
        // there before reading it here.
        const variants = parseJsonFromScriptElement(form.querySelector('[data-product-variants-json]'), 'product variants');

        if (!Array.isArray(variants) || !variants.length) {
            return;
        }

        const variantDetails = parseJsonFromScriptElement(form.querySelector('[data-product-variant-details-json]'), 'product variant details') || [];
        const formState = {
            form,
            variantIdField,
            variantSelectors,
            variants,
            variantDetails,
            addButton: getAddButtonForForm(form),
            quantityInput: form.querySelector('.product-info__quantity .quantity__input'),
            quantityButtons: Array.from(form.querySelectorAll('.product-info__quantity [data-quantity-button]'))
        };

        getProductFormBaseLabel(form);
        productFormVariantStates.set(form, formState);

        variantSelectors.forEach((selector) => {
            selector.addEventListener('change', () => {
                syncProductFormVariantSelection(formState);
            });
        });

        syncProductFormVariantSelection(formState);
    });
}

// Only forms inside the product page or the index product panel are submitted over AJAX.
function getProductAddForms(root = document) {
    return queryWithin(root, 'form[action*="/cart/add"]').filter((form) => {
        return Boolean(form.closest('[data-dynamic-product-page-content], .index-dynamic-panel--product'));
    });
}

function getSubmitButtonForProductForm(form, submitter = null) {
    if (submitter && form.contains(submitter) && submitter.matches('button')) {
        return submitter;
    }

    return getAddButtonForForm(form);
}

// Starts the submit state: the form joins submittingProductForms and the button is
// disabled and busy with "Adding…" until resetProductFormSubmitState ends it.
function startProductFormSubmit(form, button, label) {
    submittingProductForms.add(form);

    if (!button) {
        return;
    }

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    getAddButtonLabelElement(button).textContent = label;
}

// Ends the submit state: forms with variant selectors re-derive the purchasing state
// (label, disabled) from the selected variant, so a size chosen while the transient
// labels showed wins; other forms return to the base label.
function resetProductFormSubmitState(form, button) {
    submittingProductForms.delete(form);

    if (!button) {
        return;
    }

    button.removeAttribute('aria-busy');

    const formState = productFormVariantStates.get(form);

    if (formState) {
        syncProductFormVariantSelection(formState);
        return;
    }

    button.disabled = false;
    getAddButtonLabelElement(button).textContent = getProductFormBaseLabel(form);
}

// Shows a transient result label ("Added", "Error"), then resets after 1.2 s. Until then
// the form stays in submittingProductForms and the button disabled (L-64): the label
// describes the add that finished, not the size selected now, which may be sold out.
function showProductFormSubmitResult(form, button, label) {
    if (button) {
        button.setAttribute('aria-busy', 'false');
        getAddButtonLabelElement(button).textContent = label;
    }

    window.setTimeout(() => resetProductFormSubmitState(form, button), SUBMIT_RESULT_LABEL_DURATION);
}

// sections/product.liquid renders one empty [data-product-form-message] live region
// (role="status") beside the add button. A failed add writes its reason there (M-C31):
// the cart's [data-cart-message] regions sit in the cart panel, hidden while a product
// shows. Always in the DOM and only its text changes, so the region exists before any
// text arrives; product.css gives it no height while it is empty.
function setProductFormMessage(form, message) {
    const messageOutlet = form.querySelector('[data-product-form-message]');

    if (!messageOutlet) {
        return false;
    }

    messageOutlet.textContent = message;
    return true;
}

// The reason for a failed add, from the context runMutation hands to onError:
// CartRefreshOrchestrator.resolveMutationErrorMessage (the server's message, else the
// inventory-limit wording, else the default), or the default message when that is
// missing or returns anything but a non-empty string.
function resolveProductFormErrorMessage(errorContext) {
    const context = errorContext || {};
    const resolvedMessage = typeof orchestrator.resolveMutationErrorMessage === 'function'
        ? orchestrator.resolveMutationErrorMessage(context)
        : '';

    return (typeof resolvedMessage === 'string' && normalizeWhitespace(resolvedMessage))
        || normalizeWhitespace(context.defaultErrorMessage);
}

// The add request as JSON, the shape the cards send: /cart/add.js checks stock for a
// JSON body ("Only 2 items were added…", 422) but accepted a form-data body of 5 when
// 2 were in stock (checked 2026-09-24, M-C38). Line item properties[...] fields, if a
// product ever renders them, travel as the item's properties.
function buildProductAddPayload(formData) {
    const item = {
        id: Number(formData.get('id')),
        quantity: parseWholeNumber(formData.get('quantity')) || 1
    };
    const properties = {};

    formData.forEach((value, name) => {
        const propertyMatch = /^properties\[(.+)\]$/.exec(name);

        if (propertyMatch && typeof value === 'string') {
            properties[propertyMatch[1]] = value;
        }
    });

    if (Object.keys(properties).length) {
        item.properties = properties;
    }

    return { items: [item] };
}

function initializeProductFormSubmissions(root = document) {
    getProductAddForms(root).forEach((form) => {
        if (productFormsWithAjaxSubmit.has(form)) {
            return;
        }

        productFormsWithAjaxSubmit.add(form);
        getProductFormBaseLabel(form);

        form.addEventListener('submit', async (event) => {
            if (submittingProductForms.has(form)) {
                event.preventDefault();
                return;
            }

            const variantIdField = form.querySelector('[name="id"]');

            if (!variantIdField || !variantIdField.value) {
                return;
            }

            event.preventDefault();

            const submitButton = getSubmitButtonForProductForm(form, event.submitter || null);
            const addPayload = buildProductAddPayload(new FormData(form));

            startProductFormSubmit(form, submitButton, 'Adding…');
            setProductFormMessage(form, '');

            // runMutation never rejects: it resolves with the cart, with true when the add
            // succeeded but no cart could be read afterwards, or with null after an
            // error. onError writes the reason into this form's message region and
            // returns true so the orchestrator leaves the cart panel's region alone; a
            // form without the region hands the message back for the cart region.
            const cart = await orchestrator.runMutation({
                actionLabel: 'Product page add to cart',
                source: 'product-form',
                errorMessage: 'Failed to add item to cart. Please try again.',
                refreshRenderedContent: true,
                // A 422 can still have added the available quantity; bring the count in line.
                resyncAfterRejection: true,
                mutationRequest: () => fetch(storeUrl('cart/add.js'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(addPayload)
                }),
                onError: (errorContext) => {
                    const message = resolveProductFormErrorMessage(errorContext);

                    return message && setProductFormMessage(form, message) ? true : message;
                }
            });

            if (!cart) {
                showProductFormSubmitResult(form, submitButton, 'Error');
                return;
            }

            setProductFormMessage(form, '');

            form.dispatchEvent(new CustomEvent('product:added-to-cart', {
                bubbles: true,
                detail: {
                    cart: cart === true ? null : cart,
                    form
                }
            }));

            showProductFormSubmitResult(form, submitButton, 'Added');
        });
    });
}

function initializeProductForms(root = document) {
    initializeProductVariantControls(root);
    initializeProductFormSubmissions(root);
}

// Quantity stepper on the product page (sections/product.liquid): buttons named
// plus/minus around one input; an invalid or cleared field counts as 1, never below 1.
class ProductPageQuantityInput extends HTMLElement {
    constructor() {
        super();
        this.initialized = false;
    }

    connectedCallback() {
        if (this.initialized) {
            return;
        }

        this.input = this.querySelector('input');
        this.buttons = Array.from(this.querySelectorAll('button'));

        if (!this.input || !this.buttons.length) {
            console.error('Required elements not found in ProductPageQuantityInput');
            return;
        }

        this.initialized = true;

        this.buttons.forEach(button => {
            button.addEventListener('click', this.onButtonClick.bind(this));
        });
        this.input.addEventListener('change', this.onInputChange.bind(this));
    }

    getSafeQuantity() {
        const parsedValue = parseWholeNumber(this.input.value);

        return parsedValue === null || parsedValue < 1 ? 1 : parsedValue;
    }

    onButtonClick(event) {
        event.preventDefault();
        const button = event.currentTarget;
        const currentValue = this.getSafeQuantity();
        const newValue = button.name === 'plus' ? currentValue + 1 : Math.max(currentValue - 1, 1);

        this.input.value = String(newValue);
    }

    onInputChange() {
        this.input.value = String(this.getSafeQuantity());
    }
}

if (!customElements.get('product-page-quantity-input')) {
    customElements.define('product-page-quantity-input', ProductPageQuantityInput);
}

orchestrator.registerBehavior(initializeProductForms);

onReady(() => initializeProductForms());
})();
